import { useEffect, useRef, type ReactNode } from 'react'

interface BottomSheetProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  children: ReactNode
}

export const BottomSheet = ({ 
  isOpen, 
  onClose, 
  title, 
  children,
}: BottomSheetProps) => {
  const sheetRef = useRef<HTMLDivElement>(null)

  // Закрытие по Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />
      
      {/* Sheet */}
      <div 
        ref={sheetRef}
        className="relative bg-dark-tertiary rounded-t-3xl max-h-[90vh] flex flex-col animate-slide-up"
      >
        {/* Handle */}
        <div className="flex justify-center py-3" onClick={onClose}>
          <div className="w-10 h-1 rounded-full bg-text-muted/30" />
        </div>

        {/* Header */}
        {title && (
          <div className="px-4 pb-4">
            <h2 className="text-xl font-bold text-white">{title}</h2>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 pb-safe">
          {children}
        </div>
      </div>
    </div>
  )
}

// Модальное окно комментария водителю
interface CommentModalProps {
  isOpen: boolean
  onClose: () => void
  value: string
  onChange: (value: string) => void
}

export const CommentModal = ({ isOpen, onClose, value, onChange }: CommentModalProps) => {
  const inputRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])

  const handleSubmit = () => {
    onClose()
  }

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title="Комментарий водителю">
      <div className="space-y-4 pb-6">
        <textarea
          ref={inputRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Комментарий водителю"
          className="w-full h-32 px-4 py-3 bg-dark-secondary rounded-xl border border-primary/10 text-text-primary placeholder:text-text-muted resize-none focus:outline-none focus:border-primary/50 transition-colors"
        />
        
        <button
          onClick={handleSubmit}
          className="w-full py-4 rounded-xl font-semibold text-lg bg-dark-secondary text-white hover:bg-dark active:scale-[0.98] transition-all"
        >
          Готово
        </button>
      </div>
    </BottomSheet>
  )
}

// Упрощённое модальное окно ввода подъезда - только цифра
interface EntranceModalProps {
  isOpen: boolean
  onClose: () => void
  entrance: string
  onEntranceChange: (value: string) => void
}

export const EntranceModal = ({ 
  isOpen, 
  onClose, 
  entrance,
  onEntranceChange,
}: EntranceModalProps) => {
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])

  const handleKeyClick = (key: string) => {
    if (key === 'backspace') {
      onEntranceChange(entrance.slice(0, -1))
    } else if (key === 'clear') {
      onEntranceChange('')
    } else if (entrance.length < 3) {
      onEntranceChange(entrance + key)
    }
  }

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title="Подъезд">
      <div className="pb-6">
        {/* Поле ввода */}
        <div className="flex justify-center mb-6">
          <div className="w-32 h-16 bg-dark-secondary rounded-xl border border-primary/20 flex items-center justify-center">
            <span className="text-4xl font-bold text-white">
              {entrance || <span className="text-text-muted">—</span>}
            </span>
          </div>
        </div>

        {/* Клавиатура */}
        <div className="grid grid-cols-3 gap-2 max-w-xs mx-auto">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9', 'clear', '0', 'backspace'].map((key) => (
            <button
              key={key}
              onClick={() => handleKeyClick(key)}
              className={`h-14 rounded-xl font-semibold text-xl transition-all active:scale-95 ${
                key === 'clear' || key === 'backspace'
                  ? 'bg-dark text-text-muted'
                  : 'bg-dark-secondary text-white hover:bg-primary/20'
              }`}
            >
              {key === 'backspace' ? (
                <svg className="w-6 h-6 mx-auto" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 4H8l-7 8 7 8h13a2 2 0 002-2V6a2 2 0 00-2-2z"/>
                  <line x1="18" y1="9" x2="12" y2="15"/>
                  <line x1="12" y1="9" x2="18" y2="15"/>
                </svg>
              ) : key === 'clear' ? (
                'C'
              ) : (
                key
              )}
            </button>
          ))}
        </div>

        <button
          onClick={onClose}
          className="w-full mt-6 py-4 rounded-xl font-semibold text-lg bg-gradient-primary text-white shadow-purple hover:shadow-purple-lg active:scale-[0.98] transition-all"
        >
          Готово
        </button>
      </div>
    </BottomSheet>
  )
}

// Модальное окно планирования поездки
interface ScheduleModalProps {
  isOpen: boolean
  onClose: () => void
  selectedDate: Date | null
  onDateChange: (date: Date | null) => void
}

export const ScheduleModal = ({ isOpen, onClose, selectedDate, onDateChange }: ScheduleModalProps) => {
  const now = new Date()
  const dates = [
    { label: 'Сегодня', date: now },
    { label: 'Завтра', date: new Date(now.getTime() + 24 * 60 * 60 * 1000) },
    { label: 'Послезавтра', date: new Date(now.getTime() + 2 * 24 * 60 * 60 * 1000) },
  ]
  
  const hours = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'))
  const minutes = ['00', '10', '20', '30', '40', '50']

  const handleConfirm = () => {
    onClose()
  }

  const handleClear = () => {
    onDateChange(null)
    onClose()
  }

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title="Дата и время поездки">
      <div className="space-y-4 pb-6">
        {/* Дата */}
        <div className="flex gap-2 overflow-x-auto hide-scrollbar -mx-4 px-4">
          {dates.map((item) => (
            <button
              key={item.label}
              onClick={() => {
                const newDate = selectedDate ? new Date(selectedDate) : new Date()
                newDate.setFullYear(item.date.getFullYear(), item.date.getMonth(), item.date.getDate())
                onDateChange(newDate)
              }}
              className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                selectedDate && selectedDate.toDateString() === item.date.toDateString()
                  ? 'bg-primary text-white'
                  : 'bg-dark-secondary text-text-secondary hover:bg-dark'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>

        {/* Время */}
        <div className="flex gap-4">
          {/* Часы */}
          <div className="flex-1">
            <p className="text-text-muted text-xs mb-2 text-center">Часы</p>
            <div className="h-40 overflow-y-auto hide-scrollbar">
              {hours.map((hour) => (
                <button
                  key={hour}
                  onClick={() => {
                    const newDate = selectedDate ? new Date(selectedDate) : new Date()
                    newDate.setHours(parseInt(hour))
                    onDateChange(newDate)
                  }}
                  className={`w-full py-2 text-center text-lg transition-colors ${
                    selectedDate && selectedDate.getHours() === parseInt(hour)
                      ? 'text-white font-bold'
                      : 'text-text-muted'
                  }`}
                >
                  {hour}
                </button>
              ))}
            </div>
          </div>
          
          {/* Минуты */}
          <div className="flex-1">
            <p className="text-text-muted text-xs mb-2 text-center">Минуты</p>
            <div className="h-40 overflow-y-auto hide-scrollbar">
              {minutes.map((minute) => (
                <button
                  key={minute}
                  onClick={() => {
                    const newDate = selectedDate ? new Date(selectedDate) : new Date()
                    newDate.setMinutes(parseInt(minute))
                    onDateChange(newDate)
                  }}
                  className={`w-full py-2 text-center text-lg transition-colors ${
                    selectedDate && Math.floor(selectedDate.getMinutes() / 10) === Math.floor(parseInt(minute) / 10)
                      ? 'text-white font-bold'
                      : 'text-text-muted'
                  }`}
                >
                  {minute}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Информация о времени прибытия */}
        {selectedDate && (
          <div className="flex items-center gap-2 text-text-secondary text-sm bg-dark-secondary rounded-lg p-3">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 6v6l4 2"/>
            </svg>
            <span>
              Водитель приедет в {selectedDate.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
              -{new Date(selectedDate.getTime() + 10 * 60 * 1000).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        )}

        <div className="flex gap-3">
          {selectedDate && (
            <button
              onClick={handleClear}
              className="flex-1 py-4 rounded-xl font-semibold text-lg border border-primary/30 text-text-secondary hover:bg-dark active:scale-[0.98] transition-all"
            >
              Сейчас
            </button>
          )}
          <button
            onClick={handleConfirm}
            className="flex-1 py-4 rounded-xl font-semibold text-lg bg-dark-secondary text-white hover:bg-dark active:scale-[0.98] transition-all"
          >
            Готово
          </button>
        </div>
      </div>
    </BottomSheet>
  )
}

// Модальное окно специальных возможностей
interface AccessibilityModalProps {
  isOpen: boolean
  onClose: () => void
  options: {
    helpFindCar: boolean
    guidedog: boolean
    textOnly: boolean
    deaf: boolean
    wheelchair: boolean
  }
  onOptionsChange: (options: AccessibilityModalProps['options']) => void
}

export const AccessibilityModal = ({ isOpen, onClose, options, onOptionsChange }: AccessibilityModalProps) => {
  const toggleOption = (key: keyof typeof options) => {
    onOptionsChange({ ...options, [key]: !options[key] })
  }

  const items = [
    { key: 'helpFindCar' as const, label: 'Помогите найти машину' },
    { key: 'guidedog' as const, label: 'Перевозка собаки-поводыря' },
    { key: 'textOnly' as const, label: 'Общаюсь только текстом' },
    { key: 'deaf' as const, label: 'Не говорю, но слышу' },
    { key: 'wheelchair' as const, label: 'Буду с инвалидным креслом' },
  ]

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title="Что сообщить водителю?">
      <div className="space-y-1 pb-6">
        {items.map((item) => (
          <button
            key={item.key}
            onClick={() => toggleOption(item.key)}
            className="w-full flex items-center justify-between py-4 border-b border-primary/10"
          >
            <span className="text-text-primary">{item.label}</span>
            <div className={`w-12 h-7 rounded-full transition-colors relative ${
              options[item.key] ? 'bg-primary' : 'bg-dark-secondary'
            }`}>
              <div className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-transform ${
                options[item.key] ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </div>
          </button>
        ))}

        <div className="flex items-center gap-2 text-accent-red text-sm pt-4">
          <span>❤️</span>
          <span>За эти пожелания доплачивать не нужно</span>
        </div>

        <button
          onClick={onClose}
          className="w-full py-4 rounded-xl font-semibold text-lg bg-dark-secondary text-white hover:bg-dark active:scale-[0.98] transition-all mt-4"
        >
          Готово
        </button>
      </div>
    </BottomSheet>
  )
}

export default BottomSheet
